import React, {useState, useEffect, useRef} from "react";
import "./css/ClientStockTransactionsPopup.css";
import BonusShares from "./BonusShares";
import StockSplit from "./StockSplit"; // Import the Stock Split popup
import Demerger from "./Demerger"; // Import the Demerger popup
import Merger from "./Merger"; // Import the Merger popup
import IpoRights from "./IpoRights"; // Import the IPO/Rights popup
import Buyback from "./Buyback"; // Import the Buyback popup
import ChangePeriodPopup from "./ChangePeriodPopup"; // Import the Change Period Popup
import EditContractNote from "./EditContractNote"; // Import the Buyback popup
import BuySell from "./BuySell"; // Import the Buyback popup
import {useRefreshContext} from "../context/RefreshContext"; // Import the context
import {usePopupStack, useStackedPopup} from "../../contexts/PopupStackContext";
import { apiFetch} from "apiClient";
import { apiFetch as fetch } from "apiClient";

const ClientStockTransactionsPopup = ({symbol, clientId, setShowTransactionsPopup, currentPricePFStocks}) => {
    const [selectedPeriod, setSelectedPeriod] = useState("Till Current Financial Year (all)");
    const [showChangePeriodPopup, setShowChangePeriodPopup] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null); // ✅ Ref for dropdown container
    const [fromDate, setFromDate] = useState("01/04/70");
    const [toDate, setToDate] = useState(`31/03/${new Date().getFullYear() + 1}`);
    const [tempFromDate, setTempFromDate] = useState(fromDate); // Temporary state for popup
    const [tempToDate, setTempToDate] = useState(toDate);
    const [showBonusPopup, setShowBonusPopup] = useState(false);
    const [showStockSplitPopup, setShowStockSplitPopup] = useState(false);
    const [showDemergerPopup, setShowDemergerPopup] = useState(false);
    const [showMergerPopup, setShowMergerPopup] = useState(false);
    const [showIPOPopup, setShowIPOPopup] = useState(false);
    const [showBuybackPopup, setShowBuybackPopup] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [contractNote, setContractNote] = useState(null);
    const [charges, setCharges] = useState([]);
    const [showContractNotePopup, setShowContractNotePopup] = useState(false);
    const [showBuySellPopup, setShowBuySellPopup] = useState(false);
    const {refreshTrigger} = useRefreshContext(); // 🔹 Subscribe to global refresh
    const [isDisplayClick, setIsDisplayClick] = useState(false); //true when oppening an existing entry of bonus, transaction, merger, etc... false when we are opening those popups to actually do entries.
    const theadRef = useRef(null);
    const [columnWidths, setColumnWidths] = useState([]);
    const [netQuantity, setNetQuantity] = useState(0);
    const [amountInvested, setAmountInvested] = useState(0);
    /*const { push, pop } = usePopupStack();*/

    useEffect(() => {
        if (theadRef.current) {
            const widths = Array.from(theadRef.current.querySelectorAll("th")).map(th => th.offsetWidth);
            setColumnWidths(widths);
        }
    }, [transactions]); // Run when transactions change

    useEffect(() => {
        fetchTransactions();
    }, [refreshTrigger, fromDate, toDate]);
    // 🔹 Handle click outside to close dropdown
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        }

        if (showDropdown) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showDropdown]);

/*    useEffect(() => {
        const popupEntry = { onClose: () => setShowTransactionsPopup(false) }; // replace `setShowPopup`
        push(popupEntry);
        return () => pop();
    }, []);*/

    useStackedPopup(true, () => {
        setShowTransactionsPopup(false);
    });
    const handleClose = () => {
        setShowTransactionsPopup(false);
    };

    const fetchTransactions = async () => {
        try {
            // ✅ Convert DD/MM/YYYY → YYYY-MM-DD
            const formattedFromDate = fromDate.split("/").reverse().join("-"); // "01/04/70" → "70-04-01"
            const formattedToDate = toDate.split("/").reverse().join("-");     // "31/03/2026" → "2026-03-31"

            const from = new Date(formattedFromDate);
            const to = new Date(formattedToDate);

            if (isNaN(to)) {
                console.error("❌ Error: 'to' date is invalid after conversion", formattedToDate);
                return;
            }

            const qs = new URLSearchParams({
                symbol,
                clientId,
                fromDate,
                toDate
            }).toString();

            const res = await fetch(`/api/portfolio/fetchTransactions?${qs}`);
            if (!res.ok) {
                throw new Error(`API request failed with status: ${res.status}`);
            }

            let data = await res.json();
            //console.log("📌 API Response:", data); // ✅ Log API response

            // ✅ Ensure frontend also filters transactions based on selected period
            data = data.filter(txn => {
                const txnDate = new Date(txn.tradeDate);
                return txnDate >= from && txnDate <= to;
            });

            const sortedTransactions=data.sort((a, b) => {
                const dateComparison = new Date(a.tradeDate) - new Date(b.tradeDate);

                // If tradeDate is the same, prioritize SPLIT transaction
                if (dateComparison === 0) {
                    if (a.transactionType === "SPLIT" && b.transactionType !== "SPLIT") {
                        return -1; // a comes first
                    } else if (a.transactionType !== "SPLIT" && b.transactionType === "SPLIT") {
                        return 1; // b comes first
                    }
                }

                return dateComparison;
            })
            // If tradeDate is the same, prioritize SPLIT transaction
            setTransactions(sortedTransactions);

            const computedNetQuantity = sortedTransactions.reduce((sum, txn) => {
                if (txn.transactionType === "Demerger") {
                    // Reset sum to txn.demergerDetails.quantity when "Demerger" is encountered
                    return txn.netQuantity || 0;
                }
                return sum + (txn.netQuantity || 0);
            }, 0);
            setNetQuantity(computedNetQuantity);
            /*const amountInvested = transactions.reduce((sum, txn) => sum + (txn.buyTotalValue || txn.sellTotalValue || 0), 0);*/
            const computedAmountInvested = sortedTransactions.reduce((sum, txn) => {
                if (txn.transactionType === "Demerger") {
                    // Reset sum to txn.demergerDetails.totalValue when "Demerger" is encountered
                    return txn.totalValue || 0;
                }
                if (txn.transactionType === "Merger") {
                    // Reset sum to 0 totalValue when "Merger" is encountered
                    return 0;
                }
                return sum - (txn.netObligation || 0);
            }, 0);
            setAmountInvested(computedAmountInvested);

        } catch (error) {
            console.error("Error fetching transactions:", error);
        }
    };

    const handlePeriodChange = (event) => {
        const selected = event.target.value;
        setSelectedPeriod(selected);

        setTempFromDate("");
        setTempToDate("");

        const today = new Date();
        const currentYear = today.getFullYear();

        // ✅ Determine correct financial year based on the current month
        const fyStartYear = today.getMonth() < 3 ? currentYear - 1 : currentYear;
        const fyEndYear = fyStartYear + 1;
        const lastFyStartYear = fyStartYear - 1;
        const lastFyEndYear = fyStartYear;

        if (selected === "Current Financial Year") {
            setTempFromDate(`01/04/${fyStartYear}`);
            setTempToDate(`31/03/${fyEndYear}`);
        } else if (selected === "Last Financial Year") {
            setTempFromDate(`01/04/${lastFyStartYear}`);
            setTempToDate(`31/03/${lastFyEndYear}`);
        } else if (selected === "Till Current Financial Year (all)") {
            setTempFromDate("01/04/70");
            setTempToDate(`31/03/${fyEndYear}`);
        } else if (selected === "User Defined") {
            // Do not change tempFromDate and tempToDate; allow user input
        }
    };

    const handleOkClick = () => {
        if (selectedPeriod === "User Defined" && (!tempFromDate || !tempToDate)) {
            alert("Please select both From and To dates.");
            return;
        }
        setFromDate(tempFromDate);  // Apply selected period to main popup
        setToDate(tempToDate);
        setShowChangePeriodPopup(false);
    };

    // Calculate Closing Balance
    /*const netQuantity = transactions.reduce((sum, txn) => sum + (txn.buyQuantity || txn.sellQuantity || 0), 0);*/
/*    setNetQuantity(transactions.reduce((sum, txn) => sum + (txn.netQuantity || 0), 0));
    /!*const amountInvested = transactions.reduce((sum, txn) => sum + (txn.buyTotalValue || txn.sellTotalValue || 0), 0);*!/
    const amountInvested = (-1) * transactions.reduce((sum, txn) => sum + (txn.netObligation || 0), 0);*/

    const handleTransactionClick = (txn) => {
        setSelectedTransaction(txn);
    };

    const handleEditClick = async () => {
        if (!selectedTransaction) {
            alert("Please select a transaction first.");
            return;
        }

        try {
            const contractNoteNo = selectedTransaction.contractNoteNo;
            const transactionType = selectedTransaction.transactionType;
            // Fetch ContractNoteDTO in a single API call

            const qs = new URLSearchParams({ contractNoteNo }).toString();
            const res = await fetch(`/api/portfolio/fetchContractNote?${qs}`);

            if (!res.ok) {
                alert("Contract Note not found.");
                return;
            }

            const contractNoteDTO = await res.json();
                setContractNote(contractNoteDTO); // Store contract note

                // ✅ Open different popups based on CnoteType
                setIsDisplayClick(true);
                switch (contractNoteDTO.cnoteType) {
                    case "BULKIMPORT":
                    case "CNOTE":
                        setShowContractNotePopup(true);
                        break;
                    case "BONUS":
                        setShowBonusPopup(true);
                        break;
                    case "SPLIT":
                        setShowStockSplitPopup(true);
                        break;
                    case "DEMERGER":
                        setShowDemergerPopup(true);
                        break;
                    case "MERGER":
                        setShowMergerPopup(true);
                        break;
                    case "IPO":
                    case "RIGHTS":
                        setShowIPOPopup(true);
                        break;
                    case "BUYBACK":
                        setShowBuybackPopup(true);
                        break;
                    default:
                        setShowContractNotePopup(true); // Open default EditContractNote popup
                        break;
                }
        } catch (error) {
            console.error("Error fetching contract note details:", error);
            alert("Failed to fetch contract note details.");
        }
    };

    const handleBuySellClick = async () => {
        setShowBuySellPopup(true);
    };
    const formatDisplayDate = (ddmmyy) => {
        const [day, month, year] = ddmmyy.split("/");
        const date = new Date(`${year}-${month}-${day}`);
        return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    };


    return (
        <div className="popup-overlay second-popup">
            <div className="popup-content large" onClick={(e) => e.stopPropagation()}>
                <div className="popup-header">
                    <h3>{symbol} - Transactions</h3>
                    <h3>{clientId}</h3>
                </div>

                {/* Ensure header-container is properly closed before the table */}
                <div className="header-container">
                    <div className="period-header">
                        <div className="period-label">
                            <span style={{fontWeight: "500"}}>Period:</span>
                            <span>{formatDisplayDate(fromDate)} To {formatDisplayDate(toDate)}</span>
                        </div>
                        <button type="button" className="button-general"
                                onClick={() => setShowChangePeriodPopup(true)}>Change Period
                        </button>
                    </div>

                    <div className="header-buttons">
                        <button type="button" className="button-general" onClick={handleBuySellClick}>Buy Sell
                        </button>
                        <button type="button" className="button-general" onClick={handleEditClick}>Edit CNote</button>
                        <div className="dropdown" ref={dropdownRef}>
                            <button type="button" className="dropdown-button"
                                    onClick={() => setShowDropdown(!showDropdown)}>Other Transactions ▼
                            </button>
                            {showDropdown && (
                                <ul className="dropdown-menu">
                                    <li onClick={() => {
                                        setIsDisplayClick(false);
                                        setShowDropdown(false);
                                        setShowBonusPopup(true)
                                    }}>Add Bonus Received
                                    </li>
                                    <li onClick={() => {
                                        setIsDisplayClick(false);
                                        setShowDropdown(false);
                                        setShowStockSplitPopup(true)
                                    }}>Add Stock-Split Details
                                    </li>
                                    <li onClick={() => {
                                        setIsDisplayClick(false);
                                        setShowDropdown(false);
                                        setShowDemergerPopup(true)
                                    }}>Add Stock-D'Merger Details
                                    </li>
                                    <li onClick={() => {
                                        setIsDisplayClick(false);
                                        setShowDropdown(false);
                                        setShowMergerPopup(true)
                                    }}>Add Merger Details
                                    </li>
                                    <li onClick={() => {
                                        setIsDisplayClick(false);
                                        setShowDropdown(false);
                                        setShowIPOPopup(true)
                                    }}>IPO, Rights Issue
                                    </li>
                                    <li onClick={() => {
                                        setIsDisplayClick(false);
                                        setShowDropdown(false);
                                        setShowBuybackPopup(true)
                                    }}>Buyback
                                    </li>
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
                <div className="table-wrapper-client-stock-transactions">
                    <div className="transactions-container">
                        {/* Table Section */}
                        <table className="main-table" border="1" cellPadding="5" cellSpacing="0">
                            <thead>
                            <tr>
                                <th>Trade Date</th>
                                <th>Type</th>
                                <th>Quantity</th>
                                <th>Price</th>
                                <th>Brokerage (Rs.)</th>
                                <th>Amount</th>
                                <th>Bal. Quant</th>
                            </tr>
                            </thead>
                            <tbody>
                            {transactions.length === 0 ? (
                                <tr>
                                    <td colSpan="7" style={{textAlign: "center", fontWeight: "bold", color: "red"}}>
                                        No transactions found for this period.
                                    </td>
                                </tr>
                            ) : (
                                (() => {
                                    let cumulativeNetQuantity = 0;
                                    return transactions.map((txn, index) => {
                                        if (txn.transactionType === "Demerger") {
                                            // Reset cumulativeNetQuantity when "Demerger" appears
                                            cumulativeNetQuantity = txn.netQuantity || 0;
                                        } else {
                                            // Continue summing netQuantity for other transactions
                                            cumulativeNetQuantity += txn.netQuantity || 0;
                                        }

                                        return (
                                            <tr key={index}
                                                className={selectedTransaction === txn ? "selected-row" : ""}
                                                onClick={() => handleTransactionClick(txn)}
                                                style={{
                                                    cursor: "pointer"
                                                }}>
                                                <td>{txn.tradeDate ? new Date(txn.tradeDate).toLocaleDateString() : "N/A"}</td>
                                                <td>{txn.transactionType}</td>
                                                <td>{txn.netQuantity}</td>
                                                <td>
                                                {txn.weightedAvgPrice ? txn.weightedAvgPrice.toFixed(2) : "0.00"}
                                                </td>
                                                <td>
                                                    {txn.brokerage ? txn.brokerage.toFixed(2) : "0.00"}
                                                </td>
                                                <td>
                                                    {/* netObligation won't be there for split, demerger etc, then we take total value */}
                                                    {((txn.netObligation !== undefined && txn.netObligation !== null)
                                                        ? txn.netObligation * -1
                                                        : txn.totalValue ?? 0).toFixed(2)}
                                                </td>
                                                <td>
                                                    {cumulativeNetQuantity ? cumulativeNetQuantity.toFixed(3) : "0.00"}
                                                </td>
                                            </tr>
                                        );
                                    });
                                })()
                            )}
                            </tbody>
                        </table>
                    </div>

                    <div className="bottom-table-container">
                        {/* Fixed Closing Balance Footer */}
                        <table className="transactions-table" cellPadding="0" cellSpacing="0">
                            <tfoot>
                            <tr className="closing-balance">
                                <td style={{width: "14%"}}><strong>Closing Bal:</strong></td>
                                <td style={{width: "13.19%"}}></td>
                                <td style={{width: "14.92%", textAlign: "right"}}><strong>{netQuantity.toFixed(0)}</strong>
                                </td>
                                <td style={{width: "13.2%", textAlign: "right"}}>
                                    <strong>{netQuantity && netQuantity !== 0 ? (amountInvested / netQuantity).toFixed(2) : 0}</strong>
                                </td>
                                <td style={{width: "14.85%"}}></td>
                                <td style={{width: "14.92%", textAlign: "right"}}>
                                    <strong>{amountInvested.toFixed(2)}</strong>
                                </td>
                                <td style={{width: "15%"}}></td>

                            </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
                    <div className="contract-buttons">
                        <button className="cancel-button" type="button"
                                onClick={handleClose}>Close
                        </button>
                    </div>
                </div>

                {/* Popups Section */
                }
                {
                    showChangePeriodPopup && (
                        <ChangePeriodPopup
                            selectedPeriod={selectedPeriod}
                            tempFromDate={tempFromDate}
                            tempToDate={tempToDate}
                            handlePeriodChange={handlePeriodChange}
                            setTempFromDate={setTempFromDate}
                            setTempToDate={setTempToDate}
                            handleOkClick={handleOkClick}
                            onClose={() => setShowChangePeriodPopup(false)}
                        />
                    )
                }
                {
                    showBonusPopup && (
                        <BonusShares symbol={symbol} onClose={() => {
                            setShowBonusPopup(false);  // ✅ Close the popup
                            setIsDisplayClick(false);
                        }}
                                     onSave={(bonusData) => console.log("Bonus Data Saved:", bonusData)} clientId={clientId}
                                     isDisplayClick={isDisplayClick} contractNoteDTO={contractNote}/>
                    )
                }
                {
                    showStockSplitPopup && (
                        <StockSplit symbol={symbol} beforeSplitQuantityPassed={netQuantity}
                                    onClose={() => {
                                        setShowStockSplitPopup(false);  // ✅ Close the popup
                                        setIsDisplayClick(false);
                                    }}
                                    onSave={(splitData) => console.log("Stock Split Saved:", splitData)} clientId={clientId}
                                    isDisplayClick={isDisplayClick} contractNoteDTO={contractNote}/>
                    )
                }
                {
                    showDemergerPopup && (
                        <Demerger symbol={symbol} totalQuantity={netQuantity} totalAmount={amountInvested}
                                  onClose={() => {
                                      setShowDemergerPopup(false);  // ✅ Close the popup
                                      setIsDisplayClick(false);
                                  }}
                                  onSave={(demergerData) => console.log("Demerger Saved:", demergerData)}
                                  isDisplayClick={isDisplayClick} contractNoteDTO={contractNote} clientId={clientId}/>
                    )
                }
                {
                    showMergerPopup && (
                        <Merger symbol={symbol} totalQuantity={netQuantity} totalAmount={amountInvested}
                                onClose={() => {
                            setShowMergerPopup(false);  // ✅ Close the popup
                            setIsDisplayClick(false);
                        }}
                                onSave={(mergerData) => console.log("Merger Saved:", mergerData)}
                                isDisplayClick={isDisplayClick} contractNoteDTO={contractNote} clientId={clientId}/>
                    )
                }
                {
                    showIPOPopup && (
                        <IpoRights symbol={symbol} onClose={() => {
                            setShowIPOPopup(false);  // ✅ Close the popup
                            setIsDisplayClick(false);
                        }}
                                   onSave={(ipoData) => console.log("IPO/Rights Data Saved:", ipoData)}
                                   isDisplayClick={isDisplayClick} contractNoteDTO={contractNote} clientId={clientId}/>
                    )
                }
                {
                    showBuybackPopup && (
                        <Buyback symbol={symbol} onClose={() => {
                            setShowBuybackPopup(false);  // ✅ Close the popup
                            setIsDisplayClick(false);
                        }}
                                 onSave={(buybackData) => console.log("Buyback Data Saved:", buybackData)}
                                 isDisplayClick={isDisplayClick} contractNoteDTO={contractNote} clientId={clientId}/>
                    )
                }
                {
                    showContractNotePopup && (
                        <EditContractNote clientId={clientId} contractNoteDTO={contractNote}
                                          onClose={() => {
                                              setShowContractNotePopup(false);
                                              setIsDisplayClick(false);
                                          }}/>
                    )
                }
                {
                    showBuySellPopup && (
                        <BuySell clientId={clientId} onClose={() => {
                            setShowBuySellPopup(false);
                            setIsDisplayClick(false);
                        }} script={symbol} isDisplayClick={isDisplayClick}
                                 contractNoteDTO={contractNote}/>
                    )
                }
            </div>
            )
            ;
            };

            export default ClientStockTransactionsPopup;
